import { gcApi } from 'config/axios';
import { takeEvery, call, put, all, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';

import {
    FETCH_ACTIVITY_DATA,
    FETCH_CHILD_ACTIVITY_DATA_START,
    PUT_RECONSTRUCTED_ACTIVITY_DATA,
    SET_ACTIVE_LEG,
    FETCH_ACTIVITY_DATA_USING_V1_ENDPOINT,
    DELETE_LEG_SAGA,
    FETCH_SINGLE_SPORT_ACTIVITY
} from 'actions/ActionTypes';

import MetricsUtils from 'utils/MetricsUtils';
import {
    fetchActivityData,
    fetchActivityDataSuccess,
    fetchActivityDataFail,
    fetchAllChildsMetricsDataSuccess,
    fetchChildActivityDataFail,
    fetchChildActivityDataSuccess,
    makeLegsSnapshot,
    setActiveLeg,
    deleteLegSaga,
    setCurrentLegIndex,
    putReconstructedActivityDataStart,
    putReconstructedActivityDataSuccess,
    putReconstructedActivityDataFail,
    putReconstructedActivityData,
    fetchActivityDataStart,
    deleteLeg,
    fetchSingleSportActivity
} from 'actions/multiSportEditActions';
import { selectActivityData } from 'reducers/multiSportEdit/multiSportSelectors';
import NavigationUtil from 'utils/NavigationUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import { fetchActivityDataUsingV1EndpointSaga } from './MultiSport/fetchUsingV1EndpointSaga';
import {
    LEGS_SNAPSHOT_LAST_OPERATION,
    LEGS_SNAPSHOT_SET_ACTIVE_LEG
} from 'reducers/multiSportEdit/multiSportConstants';

/** @typedef {import('reducers/multiSportEdit/types/entity/MultiSportState').MultiSportState} MultiSportState */
/** @typedef {import('reducers/multiSportEdit/types/entity/RootState').RootState} RootState */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityDataDTO').ActivityDataDTO} ActivityDataDTO */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityMetricsDTO').ActivityMetricsDTO} ActivityMetricsDTO */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityMetricsDTO_v2').ActivityMetricsDTO_v2} ActivityMetricsDTO_v2 */

const { DEFAULT_MAX_CHART_SIZE, DEFAULT_MAX_POLYLINE_SIZE } = MetricsUtils;
const maxChartSize = 200; // DEFAULT_MAX_CHART_SIZE;
const maxPolylineSize = 200; // DEFAULT_MAX_POLYLINE_SIZE;

const TIMEOUT_V2 = 30000;

/************************************************************************************************************************
 * Utility functions
 ************************************************************************************************************************/

export function updatePollURLFromResponse(location) {
    let proxyWordLocation = location.indexOf('proxy/');
    if (proxyWordLocation > -1) {
        return location.substring(proxyWordLocation + 5); // Strip API host, plus five for removing the proxy word
    }
    return updatePollURL(location);
}

/**
 * @param {number[]} childActivityIds
 */
export function* fetchChildActivityData(childActivityIds) {
    try {
        yield put({
            type: FETCH_CHILD_ACTIVITY_DATA_START
        });

        const responses = yield all(
            childActivityIds.map((childId) => call(gcApi.get, `/activity-service/activity/${childId}`))
        );

        /** @type {ActivityDataDTO[]} */
        const childActivities = responses.map((response) => response.data);
        yield put(fetchChildActivityDataSuccess({ childActivities }));

        return childActivities;
    } catch (e) {
        yield put(fetchChildActivityDataFail());
    }
}

/**
 * Fetches metrics for all child activites in one big chunk
 * @param {*} activityId
 */
function* fetchAllChildsMetrics(activityId) {
    /** @type {{ data: ActivityMetricsDTO & ActivityMetricsDTO_v2 }} */
    const response = yield call(gcApi.get, `/activity-service/activity/v2/${activityId}/reconstruct`, {
        timeout: TIMEOUT_V2
    });
    yield put(
        fetchAllChildsMetricsDataSuccess({
            activityMetricsDTO: response.data
        })
    );
}

function* selectMultiSportState() {
    /** @type {RootState} */
    const rootState = yield select();
    return rootState.multiSportActivitySummary;
}

function updatePollURL(location) {
    return location.substring(location.indexOf('/', 10)); // Strip API host
}

/** fetches a parent activity's metrics data */
function* fetchParentMetricsData(activityId) {
    const metricsParameters =
        `maxChartSize=${maxChartSize}` +
        `&maxPolylineSize=${maxPolylineSize}` +
        '&metric=sumDistance' +
        '&metric=sumDuration';
    /** @type {{ data: ActivityMetricsDTO }} */
    const response = yield call(gcApi.get, `/activity-service/activity/${activityId}/details?${metricsParameters}`);
    return response.data;
}

/************************************************************************************************************************
 * Sagas
 ************************************************************************************************************************/

/**
 * Dispatch actions for fetching:
 * -parent activity data, parent metrics
 * -child activity data, child metrics
 *
 * @param {ReturnType<typeof fetchActivityData>} action
 */
function* fetchActivityDataSaga(action) {
    const { activityId } = action.payload;
    try {
        yield put(fetchActivityDataStart());
        const activityDataResponse = yield call(gcApi.get, `/activity-service/activity/${activityId}`);
        yield put(fetchActivityDataSuccess({ activityData: activityDataResponse.data }));

        if (activityDataResponse.data.isMultiSportParent) {
            const { childIds } = activityDataResponse.data.metadataDTO;
            yield call(fetchChildActivityData, childIds);
            yield call(fetchAllChildsMetrics, activityId);
        }
    } catch (error) {
        yield put(fetchActivityDataFail(/** @type {{response: {status: number}}} */ (error)));
    }
}

/**
 * @param {ReturnType<typeof fetchSingleSportActivity>} action
 */
function* fetchSingleSportActivitySaga(action) {
    const { activityId } = action.payload;
    try {
        yield put(fetchActivityDataStart());

        const activityDataResponse = yield call(gcApi.get, `/activity-service/activity/${activityId}`);
        const metricsParameters = `maxChartSize=${maxChartSize}` + `&maxPolylineSize=${maxPolylineSize}`;
        const metricsResponse = yield call(
            gcApi.get,
            `/activity-service/activity/${activityId}/details?${metricsParameters}`
        );

        /** @type {{ activityData: ActivityDataDTO; metricsData: ActivityMetricsDTO; }} */
        const payload = {
            activityData: activityDataResponse.data,
            metricsData: metricsResponse.data
        };
        yield put(fetchActivityDataSuccess(payload));

        // since this is a single sport activity, for the sake of correctly loading the store with data
        // simulate one child, which would be the activity itself
        const childIds = [+activityId];
        yield call(fetchChildActivityData, childIds);
        yield call(fetchAllChildsMetrics, activityId);
    } catch (error) {
        yield put(fetchActivityDataFail(error));
    }
}

/**
 * @param {ReturnType<typeof putReconstructedActivityData>} action
 */
function* putReconstructedActivityDataSaga(action) {
    const { selectReconstructDTOs, navigationParams, isConvertOperation } = action.payload;

    yield put(putReconstructedActivityDataStart());

    /** @type {MultiSportState} */
    const multiSportState = yield selectMultiSportState();
    const { reconstructDTOs: reconstructRequest, analyticsData, convertAnalyticsString } = selectReconstructDTOs(
        multiSportState
    );
    const { activityId } = selectActivityData(multiSportState);

    const data = { reconstructRequest };
    const config = { timeout: TIMEOUT_V2 };

    try {
        const response = yield call(gcApi.put, `/activity-service/activity/v2/${activityId}/reconstruct`, data, config);

        // still processing
        if (response.status === 202) {
            // start polling
            yield call(pollForStatusSaga, response.headers);
            yield put(putReconstructedActivityDataSuccess());
            const url = !isConvertOperation
                ? [`activity/${activityId}`, navigationParams].filter(Boolean).join('?')
                : [`activity/manual/${activityId}/edit`, navigationParams].filter(Boolean).join('?');
            NavigationUtil.navigate(url);
        }
        // analytics data
        if (isConvertOperation) {
            AnalyticsUtil.push({
                event: 'track_event',
                category: 'Advanced Multisport Editing',
                action: 'Convert to Multisport',
                label: convertAnalyticsString
            });
        }
        if (analyticsData && !isConvertOperation) {
            const { addedLegsCount, deletedLegsCount, editedLegsCount } = analyticsData;
            AnalyticsUtil.push({
                event: 'track_event',
                category: 'Advanced Multisport Editing',
                action: 'Added legs',
                label: `count:  ${addedLegsCount}`
            });
            AnalyticsUtil.push({
                event: 'track_event',
                category: 'Advanced Multisport Editing',
                action: 'Deleted legs',
                label: `count:  ${deletedLegsCount}`
            });
            AnalyticsUtil.push({
                event: 'track_event',
                category: 'Advanced Multisport Editing',
                action: 'Edited legs',
                label: `count:  ${editedLegsCount}`
            });
        }
    } catch (e) {
        yield put(putReconstructedActivityDataFail());
    }
}

/**
 * Polls the api after a successful reconstruct request and redirects once processing is done
 * @param {{ [key: string] : string }} headers object with location and location-in-milliseconds(timeout)
 */
function* pollForStatusSaga(headers) {
    let continueTrying = false;
    const location = updatePollURLFromResponse(headers['location']);
    const timeout = headers['location-in-milliseconds'];

    const response = yield call(gcApi.get, location);

    switch (response.status) {
        case 201: // complete
            continueTrying = false;
            break;
        case 202: // processing
            continueTrying = true;
            break;
        default:
            continueTrying = false;
            break;
    }

    if (continueTrying) {
        // wait for the timeout interval and afterwards try again
        yield delay(+timeout);
        yield pollForStatusSaga(headers);
    }
}

/**
 * sets the current leg index
 * takes a snapshot of the legs' state
 * @param {ReturnType<typeof setActiveLeg>} action
 */
function* setActiveLegSaga(action) {
    const { legIndex } = action.payload;
    yield put(setCurrentLegIndex(legIndex));
    yield put(makeLegsSnapshot(LEGS_SNAPSHOT_SET_ACTIVE_LEG));
    yield put(makeLegsSnapshot(LEGS_SNAPSHOT_LAST_OPERATION));
}

/**
 *
 * @param {ReturnType<typeof deleteLegSaga>} action
 */
function* deleteLegSagaHandler(action) {
    yield put(deleteLeg(action.payload));
    yield put(makeLegsSnapshot(LEGS_SNAPSHOT_SET_ACTIVE_LEG));
    yield put(makeLegsSnapshot(LEGS_SNAPSHOT_LAST_OPERATION));
}

export const multiSportEditSagas = [
    takeEvery(FETCH_ACTIVITY_DATA, fetchActivityDataSaga),
    takeEvery(FETCH_SINGLE_SPORT_ACTIVITY, fetchSingleSportActivitySaga),
    takeEvery(PUT_RECONSTRUCTED_ACTIVITY_DATA, putReconstructedActivityDataSaga),
    takeEvery(SET_ACTIVE_LEG, setActiveLegSaga),
    takeEvery(DELETE_LEG_SAGA, deleteLegSagaHandler),
    takeEvery(FETCH_ACTIVITY_DATA_USING_V1_ENDPOINT, fetchActivityDataUsingV1EndpointSaga)
];
